interface HasId {
  id: number;
}

/**
 * Append a page of results to the ones already loaded, without repeating a row.
 *
 * Every infinite-scrolling list here grows by `list.concat(page)`, and the API has been
 * observed to return a row on two pages — it paginates a set that is being sorted and
 * filtered server-side, so a row can shift across a page boundary between requests. Two rows
 * with the same id then render with the same React key: a console warning, and a selection
 * that lands on whichever one React reconciles to.
 *
 * This lives at the source rather than in the views because three stores append pages and
 * four components read them. The views used to carry their own copies of this, which meant
 * `SetRelatedArticles` — which reads the same list and never had one — was still exposed.
 *
 * Passing `[]` as `loaded` is the first page: it still drops a row the page repeats within
 * itself, which costs nothing and means no caller has to think about which case it is in.
 *
 * The already-loaded rows win: a repeat is dropped rather than replacing the copy on screen,
 * so a row the user has interacted with is never swapped underneath them.
 */
export const appendPage = <T extends HasId>(loaded: Array<T>, page: Array<T>): Array<T> => {
  const seen = new Set<number>(loaded.map(item => item.id));

  return loaded.concat(page.filter((item) => {
    if (seen.has(item.id)) {
      return false;
    }
    seen.add(item.id);

    return true;
  }));
};
